// src/pages/SignupPage.js
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { reload, sendEmailVerification } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { linkSelfToOrg } from "../utils/orgUsers";
import { DEFAULT_ORG_ID, getOrgBrandAssets } from "../utils/brandAssets";
import { auth } from "../firebase";

const DARK_KEY = "darkMode";

export default function SignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signup, user } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(false);
  const [joined, setJoined] = useState(false);

  const org = params.get("org") || "";
  const isDark = useMemo(() => localStorage.getItem(DARK_KEY) === "true", []);
  const assets = getOrgBrandAssets(DEFAULT_ORG_ID, isDark ? "dark" : "light");
  const hero = isDark ? "/brand/nia/nia-login-dark.jpg" : "/brand/nia/nia-login-light.jpg";

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) return setError("Email is required.");
    if (!password || password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    let accountCreated = false;
    try {
      setLoading(true);
      const cred = await signup(email.trim(), password);
      accountCreated = true;
      setCreated(true);
      if (cred?.user && !cred.user.emailVerified) {
        await sendEmailVerification(cred.user);
      }
    } catch (err) {
      setError(
        accountCreated
          ? "The account was created, but the verification email could not be sent. Try signing in and request verification again."
          : "Unable to create account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const claimVerifiedInvite = async () => {
    setError("");
    setLoading(true);
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error("Sign in with the invited account before joining the organisation.");
      }
      await reload(firebaseUser);
      await firebaseUser.getIdToken(true);
      if (!firebaseUser.emailVerified) {
        throw new Error("Verify the invited email address, then try again.");
      }
      if (!org) {
        throw new Error("The organisation invite link is required.");
      }
      await linkSelfToOrg({
        uid: firebaseUser.uid,
        email: firebaseUser.email || "",
      }, org);
      setJoined(true);
    } catch (claimError) {
      setError(
        String(
          claimError?.message ||
          "The organisation invitation could not be claimed."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-hero" style={{ backgroundImage: `url(${hero})` }}>
        <div className="login-claim">
          <h1 className="login-claim-title">Nia COACH — NEUROGRES™ Coach</h1>
          <p className="login-claim-sub">
            Create your account to join your organisation.
          </p>
        </div>
      </div>

      <div className="login-panel">
        <form className="login-card" onSubmit={handleSignup} autoComplete="off">
          <div className="brand-block">
            <img src={assets.wordmarkUrl} alt="Nia COACH" className="brand-wordmark" />
          </div>

          <h2 className="login-title">Create your account</h2>
          <p className="login-sub">
            {org ? `Organisation: ${org}` : "Use the email address provided by your admin."}
          </p>
          <div className="nia-alert" style={{ margin: "0 0 12px 0", fontSize: 13, padding: "10px 12px" }}>
            If your admin already created your account, do not sign up again. Go to Login and use password reset if needed.
          </div>

          {created || (org && user?.uid) ? (
            <>
              <div className="nia-alert" style={{ marginBottom: 12 }}>
                {joined
                  ? "Email verified and organisation invitation claimed."
                  : created
                    ? "Account created. Verify the email address we sent, then return here to claim the organisation invitation."
                    : "Verify this signed-in account's email before claiming the organisation invitation."}
              </div>
              {org && !joined ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={claimVerifiedInvite}
                  disabled={loading}
                  style={{ marginBottom: 8 }}
                >
                  {loading ? "Checking verification…" : "I verified my email — join organisation"}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate("/login")}
              >
                Go to login
              </button>
            </>
          ) : (
            <>
              <input
                className="form-input"
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ marginBottom: 10 }}
                autoFocus
              />
              <input
                className="form-input"
                type="password"
                placeholder="Password (min 6 chars)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ marginBottom: 10 }}
              />
              <input
                className="form-input"
                type="password"
                placeholder="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={{ marginBottom: 10 }}
              />

              {error && (
                <div style={{ color: "var(--color-danger)", fontSize: 13, marginBottom: 8 }}>
                  {error}
                </div>
              )}

              <button
                className="btn btn-primary"
                type="submit"
                disabled={loading}
                style={{ width: "100%", marginTop: 8 }}
              >
                {loading ? "Creating…" : "Create account"}
              </button>

              <button
                type="button"
                className="btn btn-ghost"
                style={{ width: "100%", marginTop: 10 }}
                onClick={() => navigate("/login")}
              >
                Back to login
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
